import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { DatePickerWithRange } from "@/components/ui/date-picker"
import { ResponsiveContainer, PieChart, Pie, Cell, Legend } from "recharts"
import { Route as costOfGoodsRoute } from '@/routes/(inventory)/branch/_layout/accounts/payment-method-breakdown/index'
import { useNavigate, useRouter } from "@tanstack/react-router"
import { useState, useCallback } from "react"
import { DateRange } from "react-day-picker"
import dayjs from "dayjs"
import customParseFormat from "dayjs/plugin/customParseFormat";
dayjs.extend(customParseFormat);
import { convertToCurrencyRounded } from "@/utils/currency_converter"
import { ChartConfig, ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart"

const Chart = () => {
    const { type, time_compare, moving_average, moving_average_days, start_date, end_date } = costOfGoodsRoute.useSearch()
    const {currentData, overlayData} = costOfGoodsRoute.useLoaderData()
    const navigate = useNavigate();
    const router = useRouter()
    let count = 7;
    let typeDayJs: dayjs.ManipulateType = "days";
    if (type === "MONTH") {
        count = 1;
        typeDayJs = "month"
    }
    if (type === "QUATER") {
        count = 3;
        typeDayJs = "month"
    }
    if (type === "HALF_YEAR") {
        count = 6;
        typeDayJs = "month"
    }
    if (type === "YEAR") {
        count = 1;
        typeDayJs = "year"
    }
    const [dateRange, setDateRange] = useState<DateRange | undefined>({
        from: type === "CUSTOM" ? dayjs.unix(start_date!).toDate() : dayjs().subtract(count, typeDayJs).toDate(),
        to: type === "CUSTOM" ? dayjs.unix(end_date!).toDate() : dayjs().toDate(),
    })

    let finalData: Array<{ payment_method: string; total_amount: number; }> = currentData.map((item, index) => {

        return {
            payment_method: item.paymentMethod,
            total_amount: item.totalAmount,
        }
    });

    const [isCustom, setIsCustom] = useState(type === "CUSTOM")

    const handleDateChange = useCallback((newDateRange: DateRange | undefined) => {
        setDateRange(newDateRange)
    }, [])

    const handleSubmit = useCallback(() => {
        if (dateRange?.from && dateRange?.to) {
            navigate({
                search: {
                    type: "CUSTOM",
                    time_compare,
                    moving_average,
                    moving_average_days,
                    start_date: Math.round(dateRange.from.getTime() / 1000),
                    end_date: Math.round(dateRange.to.getTime() / 1000),
                },
            })
        }
    }, [navigate, dateRange, time_compare, moving_average, moving_average_days])

    const handleClear = useCallback(() => {
        setDateRange(undefined)
        navigate({
            search: {
                type,
                time_compare,
                moving_average,
                moving_average_days,
            },
        })
    }, [navigate, type, time_compare, moving_average, moving_average_days])

    const COLORS = ['#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF'];
    const chartConfig: ChartConfig = {
        ['total_amount']: {
            label: 'Total Amount',
            color: '#1458DD',
        },
    };


    return (
        <div className='flex flex-col flex-1'>
            <div className="flex items-center my-4">
                <div className="w-[2px] h-12 bg-green-700 mr-4" aria-hidden="true"></div>
                <div>
                    <h2 className="text-gray-600">Payment Method Breakdown</h2>
                    <p className="text-xl font-semibold">{convertToCurrencyRounded(
                        currentData.reduce((acc, item) => acc + item.totalAmount, 0)
                    )}</p>
                    {
                        overlayData.length > 0 && (
                            <p className="text-sm text-gray-600">
                                {"Comparision Period"}: {convertToCurrencyRounded(
                                    overlayData.reduce((acc, item) => acc + item.totalAmount, 0)
                                )}
                            </p>
                        )
                    }
                </div>
            </div>

            <div className='flex-1 min-h-0'>
                <ResponsiveContainer width="100%" height="100%">
                    <ChartContainer config={chartConfig} className="h-full w-full">
                        <PieChart>
                            <Pie
                                data={finalData}
                                cx="50%"
                                cy="50%"
                                labelLine={false}
                                label={false}
                                outerRadius={120}
                                fill="#8884d8"
                                dataKey={"total_amount"}
                                nameKey={"payment_method"}
                            >
                                {finalData.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                ))}
                            </Pie>
                            <ChartTooltip
                                content={
                                    <ChartTooltipContent
                                        indicator="dot"
                                        labelFormatter={(label) => label}
                                        formatter={(value) => {
                                            return convertToCurrencyRounded(value as number);
                                        }}
                                    />
                                }
                            />
                            <Legend 
                                align="center"
                                layout="horizontal"
                                verticalAlign="top"
                            />
                        </PieChart>
                    </ChartContainer>
                </ResponsiveContainer>
            </div>                

            <hr className="my-4" />

            <div
                className='flex items-center justify-between'
            >
                <Tabs defaultValue={type} className="w-[400px]">
                    <TabsList>
                        <TabsTrigger value="WEEK" onClick={() => {
                            navigate({
                                search: {
                                    type: "WEEK",
                                    time_compare,
                                    moving_average,
                                    moving_average_days
                                },
                            })
                        }} onMouseOver={async () => {
                            await router.preloadRoute({
                                search: {
                                    type: "WEEK",
                                    time_compare,
                                    moving_average,
                                    moving_average_days
                                },
                            })
                        }}>Week</TabsTrigger>
                        <TabsTrigger value="MONTH" onClick={() => {
                            navigate({
                                search: {
                                    type: "MONTH",
                                    time_compare,
                                    moving_average,
                                    moving_average_days
                                },
                            })
                        }} onMouseOver={async () => {
                            await router.preloadRoute({
                                search: {
                                    type: "MONTH",
                                    time_compare,
                                    moving_average,
                                    moving_average_days
                                },
                            })
                        }}>Month</TabsTrigger>
                        <TabsTrigger value="QUATER" onClick={() => {
                            navigate({
                                search: {
                                    type: "QUATER",
                                    time_compare,
                                    moving_average,
                                    moving_average_days
                                },
                            })
                        }} onMouseOver={async () => {
                            await router.preloadRoute({
                                search: {
                                    type: "QUATER",
                                    time_compare,
                                    moving_average,
                                    moving_average_days
                                },
                            })
                        }}>Quarter</TabsTrigger>
                        <TabsTrigger value="HALF_YEAR" onClick={() => {
                            navigate({
                                search: {
                                    type: "HALF_YEAR",
                                    time_compare,
                                    moving_average,
                                    moving_average_days

                                },
                            })
                        }} onMouseOver={async () => {
                            await router.preloadRoute({
                                search: {
                                    type: "HALF_YEAR",
                                    time_compare,
                                    moving_average,
                                    moving_average_days
                                },
                            })
                        }}>Half Year</TabsTrigger>
                        <TabsTrigger value="YEAR" onClick={() => {
                            navigate({
                                search: {
                                    type: "YEAR",
                                    time_compare,
                                    moving_average,
                                    moving_average_days

                                },
                            })
                        }} onMouseOver={async () => {
                            await router.preloadRoute({
                                search: {
                                    type: "YEAR",
                                    time_compare,
                                    moving_average,
                                    moving_average_days
                                },
                            })
                        }}>Year</TabsTrigger>
                    </TabsList>
                </Tabs>

                <DatePickerWithRange 
                    date={dateRange}
                    setDate={handleDateChange}
                    onSubmit={handleSubmit}
                    onClear={handleClear}
                    className="w-[300px]"
                    disabledAfter={dayjs().add(1, "D").toDate()}
                />
            </div>
        </div>
    )
}

export default Chart